/**
 * <s-product-image-zoom> — PDP image zoom preloader.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * Inside an open zoom dialog, [data-zoom-go="<dialog id>"] buttons (arrows, dots) and the
 * ArrowLeft/ArrowRight keys switch straight to another photo's dialog without a fade.
 * This component only warms the selected zoom image on hover/focus so the
 * fullscreen dialog has the high-resolution image ready when opened.
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);
    this.addEventListener('click', this.#handleGo, opts);
    this.addEventListener('keydown', this.#handleKeydown, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #handleGo = (event) => {
    if (!(event.target instanceof Element)) return;
    const go = event.target.closest('[data-zoom-go]');
    if (!go || !this.contains(go)) return;
    const current = go.closest('dialog');
    const targetId = go.getAttribute('data-zoom-go');
    if (!current || !targetId || targetId === current.id) return;
    this.#switchTo(current, targetId);
  };

  #handleKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const current = event.target instanceof Element ? event.target.closest('dialog[data-zoom-dialog][open]') : null;
    if (!current) return;
    const rtl = getComputedStyle(current).direction === 'rtl';
    const forward = (event.key === 'ArrowRight') !== rtl;
    const button = current.querySelector(forward ? '[data-zoom-next]' : '[data-zoom-prev]');
    if (!button) return;
    event.preventDefault();
    this.#switchTo(current, button.getAttribute('data-zoom-go'), forward ? '[data-zoom-next]' : '[data-zoom-prev]');
  };

  #switchTo(current, targetId, focusSelector) {
    const target = document.getElementById(targetId);
    if (!(target instanceof HTMLDialogElement)) return;
    const image = target.querySelector('img[data-zoom-image]');
    if (image instanceof HTMLImageElement) image.loading = 'eager';

    // Swap instantly: no fade between photos.
    current.style.transition = 'none';
    target.style.transition = 'none';
    current.close();
    target.showModal();
    const focusTarget = (focusSelector && target.querySelector(focusSelector)) || target.querySelector('[data-zoom-next]') || target;
    focusTarget.focus({ preventScroll: true });
    requestAnimationFrame(() => {
      current.style.transition = '';
      target.style.transition = '';
    });
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
